import type { z } from 'zod'
import type { JsonValue } from '@gridfit/core/canonical'
import type { Capability } from '@gridfit/core/principals'

/**
 * A command or query declared in a form the BROWSER can hold: name, schemas,
 * capabilities — and, for commands, whether it may be queued offline.
 * Deliberately no `handle` and no `run` (design §3.1): those are server code,
 * and a `CommandDefinition`/`QueryDefinition` object carries them, so the
 * client cannot import a definition without importing the implementation and
 * everything it reaches.
 *
 * The server's `defineCommand`/`defineQuery` consume a shape by spreading it,
 * so there is ONE literal for the name, the schemas and the capability list
 * and the two sides cannot disagree about them.
 */
export interface CommandShape<P = unknown> {
  readonly name: string
  readonly payload: z.ZodType<P>
  readonly capabilities: readonly Capability[]
  /**
   * Whether this command may be enqueued in the outbox and drained later
   * (design §6.1). Site-facing writes opt in; the commercial suite is
   * online-only by design (architecture §8), and
   * `tools/structural/offline-scope.test.ts` refuses `offline: true` on any
   * shape requiring `commercial`. Declared HERE rather than at a call site so
   * a screen cannot make a money command offline-capable by accident.
   */
  readonly offline: boolean
}

export interface QueryShape<P = unknown, R extends JsonValue = JsonValue> {
  readonly name: string
  readonly params: z.ZodType<P>
  /**
   * The row shape this query returns. Required (design §3.2): without it the
   * hooks cannot type a result, and today's `run` bodies end in `as never`,
   * which infers `R` as `never` and makes every query result untyped.
   * Also the basis of `packages/contracts/src/shapes/result.test.ts`, which
   * asserts every registered query's `run` output actually parses against it
   * — a seam nothing checked before.
   */
  readonly result: z.ZodType<R>
  readonly capabilities: readonly Capability[]
}

const declared = new Map<string, 'command' | 'query'>()

function claim(name: string, kind: 'command' | 'query'): void {
  const existing = declared.get(name)
  if (existing) throw new Error(`shape already declared: ${name} (as a ${existing})`)
  declared.set(name, kind)
}

function requireCapabilities(name: string, capabilities: readonly Capability[]): void {
  if (capabilities.length === 0) {
    throw new Error(
      `${name}: a shape must declare at least one capability — an empty list would render ` +
        'this action for every principal, including a share token, leaving only the server to refuse it',
    )
  }
}

const commands: CommandShape[] = []
const queries: QueryShape[] = []

export function commandShape<P>(shape: CommandShape<P>): CommandShape<P> {
  requireCapabilities(shape.name, shape.capabilities)
  claim(shape.name, 'command')
  commands.push(shape as unknown as CommandShape)
  return shape
}

export function queryShape<P, R extends JsonValue>(shape: QueryShape<P, R>): QueryShape<P, R> {
  requireCapabilities(shape.name, shape.capabilities)
  claim(shape.name, 'query')
  queries.push(shape as unknown as QueryShape)
  return shape
}

/** For the structural gates. Sorted so failures read the same on every run. */
export function listShapes(): {
  commands: readonly CommandShape[]
  queries: readonly QueryShape[]
} {
  const byName = (a: { name: string }, b: { name: string }) => (a.name < b.name ? -1 : 1)
  return { commands: [...commands].sort(byName), queries: [...queries].sort(byName) }
}

export function resetShapesForTests(): void {
  declared.clear()
  commands.length = 0
  queries.length = 0
}
